import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLanguage, LANGUAGES } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";
import { WAITLIST } from "@/constants/testIds";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CheckCircle2, Mountain } from "lucide-react";

const BG_IMG =
  "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=2400&q=80";

export default function Waitlist() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState(lang);
  const [interest, setInterest] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/waitlist", { name, email, language, interest });
      setDone(true);
      toast.success(t("waitlist.successToast"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={WAITLIST.root} className="min-h-screen relative overflow-hidden bg-slate-950 text-white">
      {/* Mountain bg */}
      <img
        src={BG_IMG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-45"
      />
      {/* Vertical vignette gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/30 to-slate-950/90" />
      {/* Golden peak glare — angled beam */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[140vw] h-[80vh] rotate-[8deg] bg-gradient-to-b from-amber-300/50 via-amber-200/10 to-transparent blur-3xl opacity-70" />
      <div className="pointer-events-none absolute top-32 left-[52%] w-[440px] h-[440px] rounded-full bg-amber-100/40 blur-3xl" />

      {/* Content */}
      <div className="relative">
        <header className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
          <Link to="/waitlist" className="flex items-center gap-3">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/x15oz6g3_Peak%20Market%20Society%20Logo.png"
              alt="Peak Market Society"
              className="w-10 h-10 rounded-full object-contain bg-white ring-1 ring-white/20"
            />
            <div>
              <div className="font-display font-black text-white tracking-tight text-lg leading-none">
                Peak Market Society
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold mt-1">
                {t("nav.tagline")}
              </div>
            </div>
          </Link>
          <LanguageSwitcher />
        </header>

        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-amber-400 font-semibold mb-6">
              <Mountain size={14} />
              {t("waitlist.eyebrow")}
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              {t("waitlist.headlineLine1")}<br />
              <span className="bg-gradient-to-r from-amber-300 via-amber-200 to-white bg-clip-text text-transparent">
                {t("waitlist.headlineLine2")}
              </span>
            </h1>
            <p className="mt-8 text-lg text-slate-200 max-w-lg leading-relaxed">
              {t("waitlist.body")}
            </p>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,0.6)]" />
                <span>{t("waitlist.launchTag")}</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                🇺🇸 🇪🇸 🇫🇷 <span>{t("waitlist.langsAvailable")}</span>
              </div>
            </div>
          </div>

          {done ? (
            <div
              data-testid={WAITLIST.successMessage}
              className="fade-up bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-10 shadow-2xl text-center"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/20 border border-amber-400/40 mb-5">
                <CheckCircle2 className="text-amber-300" size={26} />
              </div>
              <h2 className="font-display font-bold text-3xl text-white tracking-tight">
                {t("waitlist.successTitle")}
              </h2>
              <p className="mt-4 text-slate-200">{t("waitlist.successBody")}</p>
              <p className="mt-6 text-xs uppercase tracking-widest text-amber-400 font-semibold">
                {t("waitlist.successFollow")}
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="fade-up bg-white/8 backdrop-blur-xl border border-white/15 rounded-2xl p-8 sm:p-10 shadow-2xl space-y-5"
            >
              <h2 className="font-display font-bold text-2xl text-white tracking-tight">
                {t("waitlist.formTitle")}
              </h2>
              <div>
                <Label htmlFor="wl-name" className="text-slate-200">{t("waitlist.name")}</Label>
                <Input
                  id="wl-name"
                  data-testid={WAITLIST.nameInput}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 h-11 bg-white/5 border-white/20 text-white placeholder-slate-400 focus-visible:ring-amber-400"
                />
              </div>
              <div>
                <Label htmlFor="wl-email" className="text-slate-200">{t("waitlist.email")}</Label>
                <Input
                  id="wl-email"
                  data-testid={WAITLIST.emailInput}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 h-11 bg-white/5 border-white/20 text-white placeholder-slate-400 focus-visible:ring-amber-400"
                />
              </div>
              <div>
                <Label className="text-slate-200">{t("waitlist.language")}</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger
                    data-testid={WAITLIST.languageSelect}
                    className="mt-2 h-11 bg-white/5 border-white/20 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.flag} {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="wl-interest" className="text-slate-200">{t("waitlist.interest")}</Label>
                <Textarea
                  id="wl-interest"
                  data-testid={WAITLIST.interestInput}
                  value={interest}
                  onChange={(e) => setInterest(e.target.value)}
                  rows={3}
                  className="mt-2 bg-white/5 border-white/20 text-white placeholder-slate-400 focus-visible:ring-amber-400 resize-none"
                  placeholder={t("waitlist.interestPlaceholder")}
                />
              </div>
              {error && (
                <div className="text-sm text-red-200 bg-red-500/20 border border-red-400/40 rounded-md p-3">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                data-testid={WAITLIST.submitButton}
                disabled={loading}
                className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-md"
              >
                {loading ? t("waitlist.submitting") : t("waitlist.submit")}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                {t("waitlist.privacyNote")}
              </p>
            </form>
          )}
        </div>

        <footer className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-8 text-xs text-slate-400 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Peak Market Society · {t("waitlist.footer")}</span>
          <Link to="/" className="hover:text-amber-300">{t("waitlist.enterExisting")} →</Link>
        </footer>
      </div>
    </div>
  );
}
